// Types pour le système Analytics What 2 Do ?
// Basé sur la stratégie documentée dans Analytics.md

export interface AnalyticsConfig {
  enabled: boolean;
  rgpdCompliant: boolean;
  anonymizeIp: boolean;
  allowGoogleSignals: boolean;
  allowPersonalizationSignals: boolean;
  storage: 'enabled' | 'disabled';
}

// ===== SEUILS D'ENGAGEMENT =====
export const SEUILS_ENGAGEMENT = {
  utilisateur_actif: 1,                    
  consultation_valide: 3000,               // ✅ 3 secondes (mobile)
  consultation_approfondie: 30000,         // ✅ 30 secondes 
  zone_geographique_rayon: 10,             
  session_active: 30000                    
} as const;

// ===== SOURCES UTILISATEURS =====
export type SourceUtilisateur =
  | 'recherche_google'
  | 'w2d_today'
  | 'reseaux_sociaux'
  | 'url_directe';

export type TypeUtilisateur = 'organisateur' | 'touriste';
export type StatutConnexion = 'connecte' | 'anonyme';
export type PermissionGeolocalisation = 'accordee' | 'refusee' | 'manuelle';

// ===== MODE DE LANCEMENT PWA =====
export type ModeLancement = 'pwa' | 'navigateur';

// ===== ZONES GÉOGRAPHIQUES =====
export interface ZoneGeographique {
  id: string;                              // Identifiant technique (ex: "zone_45.5_2.3")
  latitude: number;                        // Centre de la zone (arrondi)
  longitude: number;                       // Centre de la zone (arrondi)
  rayon: number;                           // Toujours 10km
}

export interface LocalisationUtilisateur {
  lat: number;
  lng: number;
}

// ===== ÉVÉNEMENTS ANALYTICS =====

// Début de session
export interface DebutSessionEvent {
  source_utilisateur: SourceUtilisateur;
  type_utilisateur: TypeUtilisateur;
  statut_connexion: StatutConnexion;
  permission_geolocalisation: PermissionGeolocalisation;
  localisation_initiale?: LocalisationUtilisateur;
  localisation_utilisateur?: LocalisationUtilisateur; // Pour batch converter (precision 5)
  zone_geographique_id?: string;
  mode_lancement: ModeLancement;                 // PWA installée ou navigateur
}

// Installation PWA (événement unique)
export interface PWAInstalledEvent {
  methode_installation: 'prompt_natif' | 'menu_navigateur';
  plateforme: 'ios' | 'android' | 'desktop' | 'unknown';
  navigateur: string;
  zone_geographique_id?: string;
}

// Apparition d'événement
export interface ApparitionEvenementEvent {
  id_evenement: string;
  id_organisateur: string;
  vue_source: 'card' | 'carte_pin' | 'carte_preview'; // ✨ 'card' pour EventCard et RecurringEventCard
  methode_decouverte: 'navigation_libre' | 'apres_recherche' | 'apres_filtres' | 'apres_recherche_et_filtres';
  localisation_utilisateur: LocalisationUtilisateur | null; // ✅ RESILIENCE PATTERN - Null si pas de géoloc
  zone_geographique_id: string | null; // ✅ RESILIENCE PATTERN - Null si pas de géoloc
  duration_ms?: number; // Durée totale de visibilité accumulée (threshold 80%)
}

// Clic sur événement
export interface ClicEvenementEvent {
  id_evenement: string;
  id_organisateur: string;
  vue_source: 'card' | 'carte_pin' | 'carte_preview'; // ✨ 'card' pour EventCard et RecurringEventCard
  methode_decouverte: 'navigation_libre' | 'apres_recherche' | 'apres_filtres' | 'apres_recherche_et_filtres';
  localisation_utilisateur?: LocalisationUtilisateur; // ✅ RESILIENCE PATTERN - Optionnelle
  zone_geographique_id: string;
  temps_depuis_apparition: number;
}

// Consultation d'événement
export interface ConsultationEvenementEvent {
  id_evenement: string;
  id_organisateur: string;
  duree_consultation: number;              // en millisecondes
  type_consultation: 'rapide' | 'valide' | 'approfondie'; // <3s, 3-30s, >30s
  est_consultation_valide: boolean;        // >= 3000ms
  a_scrolle: boolean;
  sections_vues: string[];                 // ['description', 'prix', 'organisateur', 'localisation']
  actions_effectuees: string[];            // ['partage', 'favori', 'directions']
  profondeur_scroll: number;               // % de la modal scrollée
  zone_geographique_id: string;           // Zone 10km de l'utilisateur
  zone_evenement_id: string;              // Zone 10km de l'événement
  meme_zone: boolean;                     // utilisateur et événement dans même zone 10km
}

// Actions de valeur
export interface LikeEvenementEvent {
  id_evenement: string;
  id_organisateur: string;
  methode: 'swipe_droite' | 'bouton_favori';
  contexte: 'card' | 'carte' | 'modale' | 'carte_preview';  // ✨ 'card' pour les composants card
  localisation_utilisateur?: LocalisationUtilisateur; // ✅ RESILIENCE PATTERN - Optionnelle
  zone_geographique_id: string;
}

export interface DismissEvenementEvent {
  id_evenement: string;
  id_organisateur: string;
  methode: 'swipe_gauche';
  contexte: 'card' | 'carte' | 'modale' | 'carte_preview';  // ✨ 'card' pour les composants card
  localisation_utilisateur: LocalisationUtilisateur;
  zone_geographique_id: string;
}

// Recherche et filtres
export interface RechercheEvenementEvent {
  termes_recherche: string;
  nombre_resultats: number;
  zone_geographique_id: string;
  filtres_actifs: string[];               // ['date', 'prix', 'tags']
}

export interface FiltreEvenementEvent {
  filtres_appliques: {
    date?: string;
    prix?: string;
    tags?: string[];
    distance?: number;
  };
  nombre_resultats: number;
  zone_geographique_id: string;
}

// ===== CONTEXTE DE SESSION =====
export interface SessionContext {
  session_id: string;
  source_utilisateur: SourceUtilisateur;
  type_utilisateur: TypeUtilisateur;
  statut_connexion: StatutConnexion;
  permission_geolocalisation: PermissionGeolocalisation;
  localisation_initiale?: LocalisationUtilisateur;
  zones_visitees: Set<string>;
  derniere_recherche?: string;
  derniers_filtres?: string[];
  methode_decouverte_courante: 'navigation_libre' | 'apres_recherche' | 'apres_filtres' | 'apres_recherche_et_filtres';
  heure_debut: number;
  mode_lancement: ModeLancement;                 // PWA installée ou navigateur
}

// ===== TYPES D'EXPLORATION =====
export type TypeExploration = 'local' | 'proximite' | 'eloigne';

// Helper pour déterminer le type d'exploration basé sur la distance
export interface ExplorationInfo {
  type: TypeExploration;
  distance_km: number;
}

// ===== ÉVÉNEMENTS ANALYTICS ORGANISATEUR (PHASE 2) =====

// Processus de création d'événement
export interface CreationEvenementDebutEvent {
  id_organisateur: string;
  type_evenement: 'unique' | 'recurrent';
  est_premier_evenement: boolean;         // Premier événement de cet organisateur
  a_drafts_existants: boolean;
  zone_geographique_id: string;
}

export interface FormulaireBasicInfoVersDetailsEvent {
  id_organisateur: string;
  temps_passe_etape: number;
  champs_remplis: string[];
  erreurs_rencontrees: string[];
  completude_pourcentage: number;
  type_evenement?: 'unique' | 'recurrent';
  zone_geographique_id: string;
}

export interface FormulaireDetailsVersPreviewEvent {
  id_organisateur: string;
  temps_passe_etape: number;
  champs_remplis: string[];
  erreurs_rencontrees: string[];
  completude_pourcentage: number;
  type_evenement?: 'unique' | 'recurrent';
  zone_geographique_id: string;
}

export interface ChampFormulaireModifieEvent {
  id_organisateur: string;
  etape: 'basic_info' | 'details' | 'preview';
  champ: string;                          // nom du champ modifié
  valeur_longueur?: number;               // pour les champs texte
  tentatives_geolocalisation?: number;    // pour le champ location
  nombre_modifications: number;           // combien de fois ce champ a été modifié
  zone_geographique_id: string;
}

export interface CreationEvenementAbandonneeEvent {
  id_organisateur: string;
  etape_abandon: 'basic_info' | 'details' | 'preview';
  dernier_champ_modifie: string;
  temps_total_session: number;            // en secondes
  completude_finale: number;              // % total rempli avant abandon
  raison_probable: 'erreur_validation' | 'fermeture_navigateur' | 'inactivite';
  champs_remplis: string[];
  zone_geographique_id: string;
}

// Gestion des événements récurrents
export interface EvenementRecurrentConfigureEvent {
  id_organisateur: string;
  frequence: 'daily' | 'weekly';
  nombre_jours_semaine?: number;          // si weekly
  type_fin: 'date' | 'occurrences';
  duree_serie: number;                    // en jours
  nombre_occurrences?: number;
  zone_geographique_id: string;
}

// Gestion des drafts
export interface EvenementSauvegardeDraftEvent {
  id_organisateur: string;
  id_draft: string;
  etape_sauvegarde: 'basic_info' | 'details' | 'preview';
  completude_pourcentage: number;
  champs_remplis: string[];
  est_premier_draft: boolean;
  temps_avant_sauvegarde: number;         // temps passé avant la sauvegarde
  zone_geographique_id: string;
}

export interface DraftRouvertEvent {
  id_organisateur: string;
  id_draft: string;
  age_draft_jours: number;
  etape_reprise: 'basic_info' | 'details' | 'preview';
  completude_initiale: number;
  zone_geographique_id: string;
}

export interface DraftPublieEvent {
  id_organisateur: string;
  id_draft: string;
  id_evenement_final: string;
  age_draft_jours: number;
  nombre_sessions_edition: number;
  temps_total_edition: number;            // en secondes
  etape_finalisation: 'details' | 'preview';
  zone_geographique_id: string;
}

// Publication d'événement
export interface EvenementPublieEvent {
  id_organisateur: string;
  id_evenement: string;
  temps_total_creation: number;           // De début à publication (secondes)
  nombre_sessions_creation: number;       // Combien de sessions pour créer
  etait_draft: boolean;
  completude_finale: number;              // % de champs remplis
  type_evenement: 'unique' | 'recurrent';
  nombre_occurrences?: number;            // si récurrent
  zone_geographique_id: string;
}

// Navigation interface organisateur
export interface NavigationDashboardOrganisateurEvent {
  id_organisateur: string;
  page_visitee: 'events_actifs' | 'drafts' | 'events_passes' | 'profil' | 'statistiques' | 'creation';
  temps_passe: number;                    // en secondes
  actions_effectuees: string[];
  zone_geographique_id: string;
}

export interface ConsultationEventOrganisateurEvent {
  id_organisateur: string;
  id_evenement: string;
  statut_evenement: 'actif' | 'draft' | 'passe';
  temps_consultation: number;             // en secondes
  actions: string[];                      // ['modifier', 'supprimer', 'dupliquer']
  zone_geographique_id: string;
} 

// Tentative de publication échouée
export interface TentativePublicationEchoueeEvent {
  id_organisateur: string;
  etape_actuelle: 'basic_info' | 'details' | 'preview';
  champs_manquants: string[];
  completude_actuelle: number;
  temps_avant_tentative: number;
  nombre_tentatives: number;
  zone_geographique_id: string;
}

// ===== ÉVÉNEMENTS ANALYTICS PROFIL ORGANISATEUR (PHASE 4) =====

// Calcul du score de complétion du profil
export interface ProfilCompletionScoreEvent {
  id_organisateur: string;
  score_completion: number;               // % de complétion (0-100)
  elements_completes: string[];           // Liste des éléments remplis
  elements_manquants: string[];           // Liste des éléments manquants
  zone_geographique_id: string;
}

// Vue du profil public par un autre utilisateur
export interface ProfilPublicConsulteEvent {
  id_organisateur_consulte: string;
  type_visiteur: 'touriste' | 'organisateur' | 'anonyme';
  est_organisateur_visiteur: boolean;     // Si le visiteur est aussi organisateur
  elements_profil_visibles: string[];     // Quels éléments sont affichés
  temps_consultation: number;             // Temps passé sur le profil
  zone_geographique_id: string;
}

// Clics sur éléments de contact
export interface ContactProfilClicEvent {
  id_organisateur: string;
  type_contact: 'email' | 'telephone' | 'site_web' | 'instagram';
  depuis_vue: 'profil_public' | 'evenement_modal' | 'card_evenement';
  zone_geographique_id: string;
}

// Upload d'images profil
export interface ImageProfilUploadeeEvent {
  id_organisateur: string;
  type_image: 'avatar' | 'galerie';
  taille_fichier: number;                 // En bytes
  format_image: string;                   // jpg, png, etc.
  succes_upload: boolean;
  nombre_images_galerie_total?: number;   // Après upload (si galerie)
  tentative_numero: number;               // Si plusieurs tentatives
  zone_geographique_id: string;
} 